// src/context/ThemeContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

// Background theme definitions - different background styles beyond just dark/light
// Each theme includes coordinated colors for bg, cards, borders, and text
const BACKGROUND_THEMES = {
  ocean: {
    name: 'Ocean',
    description: 'Deep sea vibes',
    dark: {
      bg: '#0a1628',           // Dark navy
      bgHover: '#0f1e32',
      bgMuted: '#0f1e3299',
      surface: '#0c1a2e',
      card: '#0c1a2e',         // Deep ocean card
      cardHover: '#102440',
      border: '#1a3050',       // Ocean blue border
      borderLight: '#234060',
    },
    light: {
      bg: '#ffffff',           // White page background
      bgHover: '#e0f2fe',      // Sky-100
      bgMuted: '#f0f9ff',      // Sky-50 for headers
      surface: '#ffffff',
      card: '#ffffff',
      cardHover: '#e0f2fe',
      border: '#bae6fd',       // Sky-200
      borderLight: '#7dd3fc',  // Sky-300
    },
  },
  classic: {
    name: 'Classic',
    description: 'Clean zinc tones',
    dark: {
      bg: '#09090b',           // zinc-950 - page background
      bgHover: '#18181b',      // zinc-900 - hover states
      bgMuted: '#18181b99',    // muted backgrounds
      surface: '#09090b',      // same as bg for default
      card: '#09090b',         // card background
      cardHover: '#18181b',    // card hover
      border: '#27272a',       // zinc-800 - borders
      borderLight: '#3f3f46',  // zinc-700 - lighter borders
    },
    light: {
      bg: '#ffffff',           // White page background
      bgHover: '#f4f4f5',      // Subtle hover
      bgMuted: '#fafafa',      // Slightly muted for headers
      surface: '#ffffff',      // Pure white surface
      card: '#ffffff',         // White cards
      cardHover: '#f4f4f5',    // Light hover on cards
      border: '#e4e4e7',       // zinc-200
      borderLight: '#d4d4d8',  // zinc-300
    },
  },
  midnight: {
    name: 'Midnight',
    description: 'Deep blue-black',
    dark: {
      bg: '#0a0a14',           // Deep blue-black
      bgHover: '#12121f',
      bgMuted: '#12121f99',
      surface: '#0d0d1a',
      card: '#0d0d1a',         // Slightly lighter than bg
      cardHover: '#14142a',
      border: '#1e1e3a',       // Blue-tinted border
      borderLight: '#2a2a4a',
    },
    light: {
      bg: '#ffffff',           // White page background
      bgHover: '#f1f5f9',      // Slate-100
      bgMuted: '#f8fafc',      // Slate-50 for headers
      surface: '#ffffff',
      card: '#ffffff',
      cardHover: '#f1f5f9',
      border: '#e2e8f0',       // Slate-200
      borderLight: '#cbd5e1',  // Slate-300
    },
  },
  forest: {
    name: 'Forest',
    description: 'Dark green depths',
    dark: {
      bg: '#0a1410',           // Deep forest
      bgHover: '#0f1f18',
      bgMuted: '#0f1f1899',
      surface: '#0c1812',
      card: '#0c1812',         // Forest card
      cardHover: '#122a1f',
      border: '#1a3a28',       // Forest green border
      borderLight: '#254a35',
    },
    light: {
      bg: '#ffffff',           // White page background
      bgHover: '#dcfce7',      // Green-100
      bgMuted: '#f0fdf4',      // Green-50 for headers
      surface: '#ffffff',
      card: '#ffffff',
      cardHover: '#dcfce7',
      border: '#bbf7d0',       // Green-200
      borderLight: '#86efac',  // Green-300
    },
  },
};

// Full theme definitions with colors for both light and dark modes
// Each accent color has a complete palette for backgrounds, surfaces, and accents
const ACCENT_THEMES = {
  blue: {
    name: 'Blue',
    // Primary accent colors (RGB values for opacity support)
    primary: '59 130 246',        // blue-500
    primaryHover: '96 165 250',   // blue-400
    primaryDark: '37 99 235',     // blue-600
    ring: '59 130 246',           // blue-500
    // Extended palette
    50: '239 246 255',            // blue-50
    100: '219 234 254',           // blue-100
    200: '191 219 254',           // blue-200
    900: '30 58 138',             // blue-900
    950: '23 37 84',              // blue-950
    // Gradient colors
    gradientFrom: '59 130 246',   // blue-500
    gradientTo: '147 51 234',     // purple-600
  },
  purple: {
    name: 'Purple',
    primary: '168 85 247',        // purple-500
    primaryHover: '192 132 252',  // purple-400
    primaryDark: '147 51 234',    // purple-600
    ring: '168 85 247',
    50: '250 245 255',            // purple-50
    100: '243 232 255',           // purple-100
    200: '233 213 255',           // purple-200
    900: '88 28 135',             // purple-900
    950: '59 7 100',              // purple-950
    gradientFrom: '168 85 247',   // purple-500
    gradientTo: '236 72 153',     // pink-500
  },
  teal: {
    name: 'Teal',
    primary: '20 184 166',        // teal-500
    primaryHover: '45 212 191',   // teal-400
    primaryDark: '13 148 136',    // teal-600
    ring: '20 184 166',
    50: '240 253 250',            // teal-50
    100: '204 251 241',           // teal-100
    200: '153 246 228',           // teal-200
    900: '19 78 74',              // teal-900
    950: '4 47 46',               // teal-950
    gradientFrom: '20 184 166',   // teal-500
    gradientTo: '34 197 94',      // green-500
  },
  green: {
    name: 'Green',
    primary: '34 197 94',         // green-500
    primaryHover: '74 222 128',   // green-400
    primaryDark: '22 163 74',     // green-600
    ring: '34 197 94',
    50: '240 253 244',            // green-50
    100: '220 252 231',           // green-100
    200: '187 247 208',           // green-200
    900: '20 83 45',              // green-900
    950: '5 46 22',               // green-950
    gradientFrom: '34 197 94',    // green-500
    gradientTo: '20 184 166',     // teal-500
  },
  rose: {
    name: 'Rose',
    primary: '244 63 94',         // rose-500
    primaryHover: '251 113 133',  // rose-400
    primaryDark: '225 29 72',     // rose-600
    ring: '244 63 94',
    50: '255 241 242',            // rose-50
    100: '255 228 230',           // rose-100
    200: '254 205 211',           // rose-200
    900: '136 19 55',             // rose-900
    950: '76 5 25',               // rose-950
    gradientFrom: '244 63 94',    // rose-500
    gradientTo: '249 115 22',     // orange-500
  },
  orange: {
    name: 'Orange',
    primary: '249 115 22',        // orange-500
    primaryHover: '251 146 60',   // orange-400
    primaryDark: '234 88 12',     // orange-600
    ring: '249 115 22',
    50: '255 247 237',            // orange-50
    100: '255 237 213',           // orange-100
    200: '254 215 170',           // orange-200
    900: '124 45 18',             // orange-900
    950: '67 20 7',               // orange-950
    gradientFrom: '249 115 22',   // orange-500
    gradientTo: '234 179 8',      // yellow-500
  },
};

// Detect the user's OS-level light/dark preference at first paint, so
// new visitors who use light mode at the system level land on light
// instead of being thrown into our dark default. Once they explicitly
// toggle (or the localStorage key gets set in any other flow), we
// honor their choice on subsequent visits — system preference is only
// the seed value, not a live binding.
const getInitialTheme = () => {
  const saved = localStorage.getItem('theme');
  if (saved === 'dark' || saved === 'light') return saved;
  // No stored preference → match system. matchMedia returns false in
  // environments that don't expose the media query (SSR / very old
  // browsers), so default to dark in that case to keep the legacy
  // brand feel.
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return 'dark';
};

export const ThemeProvider = ({ children }) => {
  // Defaults when nothing saved yet:
  //   theme           — matches OS light/dark preference at first load
  //   accentColor     — blue (brand accent)
  //   backgroundTheme — classic (true black / zinc-950) — was 'ocean'
  //                     (navy) before 2026-05-30. Black + blue accent is
  //                     the new house style; existing users with 'ocean'
  //                     saved in localStorage are unaffected.
  const [theme, setTheme] = useState(getInitialTheme);
  const [accentColor, setAccentColor] = useState(() => localStorage.getItem('accentColor') || 'blue');
  const [backgroundTheme, setBackgroundTheme] = useState(() => localStorage.getItem('backgroundTheme') || 'classic');
  // Gradient surfaces toggle — when disabled, the user gets flat cards
  // and no ambient body gradient. Persisted to localStorage so it
  // applies before React hydrates (avoids a flash of gradient on load).
  const [gradientEnabled, setGradientEnabled] = useState(() => {
    const stored = localStorage.getItem('gradientEnabled');
    return stored === null ? true : stored === 'true';
  });

  // Apply theme (light/dark) and background theme
  useEffect(() => {
    const root = document.documentElement;
    const isDark = theme === 'dark';

    // Required for Tailwind dark mode (darkMode: 'class')
    root.classList.toggle('dark', isDark);

    // Keep data-theme attribute for CSS selectors
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');

    // Apply background theme colors
    const bgTheme = BACKGROUND_THEMES[backgroundTheme] || BACKGROUND_THEMES.ocean;
    const colors = isDark ? bgTheme.dark : bgTheme.light;

    // Page background colors
    root.style.setProperty('--color-bg', colors.bg);
    root.style.setProperty('--color-bg-hover', colors.bgHover);
    root.style.setProperty('--color-bg-muted', colors.bgMuted);
    root.style.setProperty('--bg-surface', colors.surface);

    // Card and content area colors
    root.style.setProperty('--color-card', colors.card);
    root.style.setProperty('--color-card-hover', colors.cardHover);

    // Border colors
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-border-light', colors.borderLight);

    // Set data attribute for background theme
    root.setAttribute('data-bg-theme', backgroundTheme);

    localStorage.setItem('theme', theme);
  }, [theme, backgroundTheme]);

  // Apply accent color CSS variables with full palette
  useEffect(() => {
    const root = document.documentElement;
    const colors = ACCENT_THEMES[accentColor] || ACCENT_THEMES.blue;

    // Primary accent colors
    root.style.setProperty('--accent-primary', colors.primary);
    root.style.setProperty('--accent-primary-hover', colors.primaryHover);
    root.style.setProperty('--accent-primary-dark', colors.primaryDark);
    root.style.setProperty('--accent-ring', colors.ring);

    // Extended palette for backgrounds and surfaces
    root.style.setProperty('--accent-50', colors[50]);
    root.style.setProperty('--accent-100', colors[100]);
    root.style.setProperty('--accent-200', colors[200]);
    root.style.setProperty('--accent-900', colors[900]);
    root.style.setProperty('--accent-950', colors[950]);

    // Gradient colors
    root.style.setProperty('--accent-gradient-from', colors.gradientFrom);
    root.style.setProperty('--accent-gradient-to', colors.gradientTo);

    // Set data attribute for CSS selectors
    root.setAttribute('data-accent', accentColor);

    localStorage.setItem('accentColor', accentColor);
  }, [accentColor]);

  // Apply background theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('backgroundTheme', backgroundTheme);
  }, [backgroundTheme]);

  // Apply the gradient toggle — adds/removes `gradients-off` on <html>.
  // App.css has rules scoped to :root.gradients-off that flatten every
  // gradient surface (body::before, .kpi-card, .theme-card, etc.).
  useEffect(() => {
    document.documentElement.classList.toggle('gradients-off', !gradientEnabled);
    localStorage.setItem('gradientEnabled', String(gradientEnabled));
  }, [gradientEnabled]);

  const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

  return (
    <ThemeContext.Provider value={{
      theme,
      toggleTheme,
      setTheme,
      accentColor,
      setAccentColor,
      accentColors: ACCENT_THEMES,
      backgroundTheme,
      setBackgroundTheme,
      backgroundThemes: BACKGROUND_THEMES,
      gradientEnabled,
      setGradientEnabled,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
